import React, { useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Upload, ScanLine, Search, UserPlus, PackageMinus, FileText, Loader2, Download, Trash2, CheckCircle2, Pencil, Check, X, ArrowLeftRight } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import BarcodeScanner from "@/components/BarcodeScanner";

export default function MagazzinoGenerale() {
  const { t } = useTranslation();
  const [serials, setSerials] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [committenti, setCommittenti] = useState([]);
  const [statusFilter, setStatusFilter] = useState("warehouse");
  const [q, setQ] = useState("");
  const [committenteFilter, setCommittenteFilter] = useState("__all__");
  const [materialFilter, setMaterialFilter] = useState("__all__");
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);

  const [scanValue, setScanValue] = useState("");
  // Focus automatico sull'input barcode dopo un'azione (per sparare barcode consecutivi)
  const scanInputRef = useRef(null);
  const focusScanInput = () => {
    setTimeout(() => {
      try { scanInputRef.current?.focus(); scanInputRef.current?.select?.(); } catch {}
    }, 100);
  };
  const [scanMaterial, setScanMaterial] = useState("");
  const [scanWarehouse, setScanWarehouse] = useState("");
  const [scanCommittente, setScanCommittente] = useState("");

  const [openAdd, setOpenAdd] = useState(false);
  const [addSerial, setAddSerial] = useState("");
  const [addMaterial, setAddMaterial] = useState("");

  const [openUpload, setOpenUpload] = useState(false);
  const [file, setFile] = useState(null);
  const [uploadMaterial, setUploadMaterial] = useState("");

  const [openPdf, setOpenPdf] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfMaterial, setPdfMaterial] = useState("");
  const [pdfCommittente, setPdfCommittente] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfExtracted, setPdfExtracted] = useState([]);

  const [openAssign, setOpenAssign] = useState(false);
  const [assignTech, setAssignTech] = useState("");

  // Inline edit committente per riga
  const [editingComm, setEditingComm] = useState(null);
  const [editingCommValue, setEditingCommValue] = useState("");
  // Dialog Modifica seriale (Seriale + Materiale + Committente)
  const [editDialog, setEditDialog] = useState(null); // { id, serial, material_name, committente_id }
  const [savingSerial, setSavingSerial] = useState(false);
  const [serialConflict, setSerialConflict] = useState(null); // { newSerial, existing }

  const saveSerial = async (opts = {}) => {
    if (!editDialog?.id) return;
    const val = (editDialog.serial || "").trim();
    if (!val) return toast.error("Il seriale non può essere vuoto");
    if (!(editDialog.material_name || "").trim()) return toast.error("Il materiale è obbligatorio");
    setSavingSerial(true);
    try {
      const { data: upd } = await api.patch(`/serials/${editDialog.id}`, {
        serial: val,
        material_name: editDialog.material_name || "",
        committente_id: editDialog.committente_id || "",
        ...(opts.forceReplace ? { force_replace_existing: true } : {}),
      });
      toast.success(`Seriale aggiornato: ${val}`);
      setEditDialog(null);
      setSerialConflict(null);
      // Optimistic: aggiorna la riga nella lista senza ricarica completa
      if (upd?.id) {
        setSerials((prev) => prev.map((x) => x.id === upd.id ? upd : x));
      } else {
        load();
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "SERIAL_CONFLICT" && ["warehouse", "traveling"].includes(detail.existing?.status)) {
        setSerialConflict({ newSerial: val, existing: detail.existing });
      } else {
        toast.error(formatApiErrorDetail(detail));
      }
    } finally { setSavingSerial(false); }
  };

  const saveComm = async (serialId) => {
    const commId = editingCommValue === "__none__" ? "" : editingCommValue;
    try {
      await api.patch(`/serials/${serialId}`, { committente_id: commId });
      toast.success(t("auto.committente_aggiornato"));
      setEditingComm(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // Dialog "seriale già presente" — mostra dove sta e offre rientro
  const [returnDialog, setReturnDialog] = useState(null);  // { info, targetSerial }

  const load = async () => {
    setLoading(true);
    try {
      const params = { status: statusFilter, q: q || undefined };
      if (committenteFilter && committenteFilter !== "__all__") params.committente_id = committenteFilter;
      const { data } = await api.get("/serials", { params });
      setSerials(data);
      setSelected(new Set());
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter, committenteFilter]);

  // Lista materiali filtrata client-side per nome (con dropdown Materiale)
  const filteredSerials = useMemo(() => {
    if (materialFilter === "__all__") return serials;
    return serials.filter((s) => (s.material_name || "") === materialFilter);
  }, [serials, materialFilter]);

  // Lista univoca dei materiali presenti nei seriali attualmente caricati (per popolare il dropdown)
  const materialsInList = useMemo(() => {
    const set = new Set();
    for (const s of serials) if (s.material_name) set.add(s.material_name);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [serials]);

  const doExport = async () => {
    try {
      const params = { status: statusFilter };
      if (committenteFilter && committenteFilter !== "__all__") params.committente_id = committenteFilter;
      if (q) params.q = q;
      const res = await api.get("/serials/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `seriali_${statusFilter}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Errore export");
    }
  };
  useEffect(() => {
    api.get("/users", { params: { role: "tecnico,subappalto" } }).then((r) => setTechnicians(r.data));
    api.get("/materials").then((r) => {
      setMaterials(r.data);
      if (r.data.length) {
        setScanMaterial(r.data[0].name);
        setAddMaterial(r.data[0].name);
        setUploadMaterial(r.data[0].name);
      }
    });
    api.get("/warehouses").then((r) => setWarehouses(r.data));
    api.get("/committenti").then((r) => setCommittenti(r.data));
  }, []);

  const doScan = async (overrideSerial) => {
    const raw = typeof overrideSerial === "string" ? overrideSerial : scanValue;
    const s = (raw || "").trim();
    if (!s) return;
    // Ottimizzazione velocità: chiamiamo direttamente POST /serials (1 sola richiesta).
    // Se il seriale esiste già, il backend risponde 400 e SOLO in quel caso facciamo lookup.
    try {
      const { data: created } = await api.post("/serials", {
        serial: s,
        material_name: scanMaterial,
        warehouse_id: scanWarehouse || null,
        committente_id: scanCommittente || null,
      });
      // Optimistic update: aggiungiamo la nuova riga in cima senza reload completo
      setSerials((prev) => [created, ...prev]);
      toast.success(`Seriale ${s} caricato`);
      setScanValue("");
      focusScanInput();
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      const isDup = typeof detail === "string" && detail.toLowerCase().includes("già presente");
      if (isDup) {
        // seriale esistente → lookup per capire dove si trova
        try {
          const { data: lookup } = await api.get("/serials/lookup", { params: { serial: s } });
          if (lookup.found) {
            if (lookup.status === "warehouse") {
              toast.info(`Seriale ${s} è già in magazzino generale`);
              setScanValue(""); focusScanInput(); return;
            }
            if (lookup.status === "installed") {
              toast.error(`Seriale ${s} risulta installato (${lookup.installed_address || lookup.installed_customer || ""}) — non ripristinabile`);
              setScanValue(""); focusScanInput(); return;
            }
            // traveling / lost → dialog rientro
            setReturnDialog({ info: lookup, targetSerial: s });
            setScanValue("");
            return;
          }
        } catch {}
      }
      toast.error(formatApiErrorDetail(detail) || "Errore");
      focusScanInput();
    }
  };

  const doAdd = async () => {
    const s = (addSerial || "").trim();
    if (!s) return toast.error("Seriale mancante");
    try {
      // 1) Lookup preventivo — evita "already exists" grezzo
      const { data: lookup } = await api.get("/serials/lookup", { params: { serial: s } });
      if (lookup.found) {
        if (lookup.status === "warehouse") {
          toast.info(`Seriale ${s} è già in magazzino generale`);
          return;
        }
        if (lookup.status === "installed") {
          toast.error(`Seriale ${s} risulta installato (${lookup.installed_address || lookup.installed_customer || ""}) — non è ripristinabile`);
          return;
        }
        // traveling o lost → apri dialog di rientro
        setReturnDialog({ info: lookup, targetSerial: s });
        return;
      }
      // 2) Non esiste → crea normale
      await api.post("/serials", { serial: s, material_name: addMaterial, committente_id: scanCommittente || null });
      toast.success("Aggiunto");
      setOpenAdd(false);
      setAddSerial("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const confirmReturn = async () => {
    if (!returnDialog) return;
    const { info } = returnDialog;
    try {
      const { data } = await api.post("/serials/unload", { serial: info.serial, reason: "Rientro manuale da magazzino generale" });
      toast.success(`Seriale ${info.serial} rientrato in magazzino generale`);
      setReturnDialog(null);
      setOpenAdd(false);
      setAddSerial("");
      focusScanInput();
      // Optimistic: se il backend ha ritornato il seriale aggiornato, aggiornalo in lista
      if (data?.serial && typeof data.serial === "object") {
        const upd = data.serial;
        setSerials((prev) => {
          const idx = prev.findIndex((x) => x.id === upd.id);
          if (idx >= 0) { const copy = [...prev]; copy[idx] = upd; return copy; }
          return [upd, ...prev];
        });
      } else {
        load();
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); focusScanInput(); }
  };

  // Ricerca "intelligente": se il seriale non è in magazzino ma è su un furgone/perso,
  // apre direttamente il dialog di rientro invece di mostrare una lista vuota.
  const doSearch = async () => {
    const term = (q || "").trim();
    if (!term) { load(); return; }
    try {
      const { data: lookup } = await api.get("/serials/lookup", { params: { serial: term } });
      if (lookup.found && (lookup.status === "traveling" || lookup.status === "lost")) {
        setReturnDialog({ info: lookup, targetSerial: lookup.serial });
        return;
      }
      if (lookup.found && lookup.status === "installed") {
        toast.info(`Seriale ${lookup.serial} risulta installato (${lookup.installed_address || lookup.installed_customer || ""})`);
      }
    } catch (_) { /* fallback su load */ }
    load();
  };


  const doUpload = async () => {
    if (!file) return toast.error(t("auto.seleziona_un_file_excelcsv"));
    const form = new FormData();
    form.append("file", file);
    try {
      const params = new URLSearchParams({ material_name: uploadMaterial });
      if (scanCommittente) params.append("committente_id", scanCommittente);
      const { data } = await api.post(`/serials/upload-excel?${params.toString()}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.count_added} seriali importati (${data.skipped.length} scartati)`);
      setOpenUpload(false);
      setFile(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const doPdfExtract = async () => {
    if (!pdfFile) return toast.error(t("auto.seleziona_il_pdf_della_bolla"));
    setPdfLoading(true);
    setPdfExtracted([]);
    const form = new FormData();
    form.append("file", pdfFile);
    try {
      const { data } = await api.post("/serials/parse-pdf", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPdfExtracted(data.serials || []);
      if (!data.serials?.length) toast.error(t("auto.nessun_seriale_rilevato_verifica_il_pdf"));
      else toast.success(`${data.serials.length} seriali rilevati — controlla e conferma`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setPdfLoading(false); }
  };

  const doPdfCommit = async () => {
    if (!pdfExtracted.length || !pdfMaterial) return toast.error(t("auto.materiale_mancante"));
    try {
      const { data } = await api.post("/serials/bulk", {
        material_name: pdfMaterial,
        serials: pdfExtracted,
        committente_id: pdfCommittente || null,
      });
      toast.success(`${data.count_added} seriali caricati (${data.skipped.length} già presenti)`);
      setOpenPdf(false);
      setPdfFile(null);
      setPdfExtracted([]);
      setPdfCommittente("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/downloads/serials-template.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "template_seriali_magazzino.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Errore download template"); }
  };

  const toggleAll = (v) => {
    if (v) setSelected(new Set(filteredSerials.map((s) => s.id)));
    else setSelected(new Set());
  };

  const doAssign = async () => {
    if (!assignTech || selected.size === 0) return toast.error(t("auto.seleziona_tecnico_e_seriali"));
    try {
      const { data } = await api.post("/serials/assign", { technician_id: assignTech, serial_ids: [...selected] });
      toast.success(`${data.updated} seriali assegnati`);
      setOpenAssign(false);
      setAssignTech("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const doUnload = async (serial) => {
    try {
      await api.post("/serials/unload", { serial, reason: "scarico manuale" });
      toast.success("Scaricato");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const doDelete = async (sid, serial) => {
    if (!window.confirm(`Eliminare definitivamente il seriale ${serial}?\nQuesta operazione non può essere annullata.`)) return;
    try {
      await api.delete(`/serials/${sid}`);
      toast.success(`Seriale ${serial} eliminato`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const doDeleteBulk = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Eliminare definitivamente ${selected.size} seriali?\nQuesta operazione non può essere annullata.`)) return;
    try {
      const { data } = await api.post("/serials/delete-bulk", { serial_ids: [...selected] });
      toast.success(`${data.deleted} seriali eliminati`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("auto.inventario")}</div>
          <h1 className="mt-1 text-3xl font-bold text-slate-900 lg:text-4xl">{t("menu.warehouse_general")}</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Dialog open={openAdd} onOpenChange={setOpenAdd}>
            <DialogTrigger asChild>
              <Button data-testid="btn-add-serial" className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]">
                <Plus className="mr-2 h-4 w-4" /> {t("auto.nuovo_seriale")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{t("auto.aggiungi_seriale")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>{t("auto.materiale_x")}</Label>
                  <Select value={addMaterial} onValueChange={setAddMaterial}>
                    <SelectTrigger className="rounded-sm" data-testid="add-material"><SelectValue placeholder={t("auto.scegli_materiale")} /></SelectTrigger>
                    <SelectContent>{materials.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("auto.committente_opzionale")}</Label>
                  <Select value={scanCommittente || "__none__"} onValueChange={(v) => setScanCommittente(v === "__none__" ? "" : v)}>
                    <SelectTrigger className="rounded-sm" data-testid="add-committente"><SelectValue placeholder={t("auto.nessuno")} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">{t("auto.nessuno")}</SelectItem>
                      {committenti.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Seriale</Label>
                  <Input data-testid="add-serial-input" value={addSerial} onChange={(e) => setAddSerial(e.target.value)} className="rounded-sm font-mono" />
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="add-serial-confirm" onClick={doAdd} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]">{t("common.add")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={openUpload} onOpenChange={setOpenUpload}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="btn-upload-excel" className="rounded-sm">
                <Upload className="mr-2 h-4 w-4" /> {t("auto.import_excelcsv")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Importa seriali</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>{t("auto.materiale_x")}</Label>
                  <Select value={uploadMaterial} onValueChange={setUploadMaterial}>
                    <SelectTrigger className="rounded-sm"><SelectValue placeholder={t("auto.scegli_materiale")} /></SelectTrigger>
                    <SelectContent>{materials.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>File Excel o CSV (colonna "serial" o prima colonna)</Label>
                  <Input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setFile(e.target.files?.[0])} className="rounded-sm" data-testid="upload-file" />
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="upload-confirm" onClick={doUpload} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]">{t("auto.importa")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={openPdf} onOpenChange={setOpenPdf}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="btn-upload-pdf" className="rounded-sm">
                <FileText className="mr-2 h-4 w-4" /> {t("auto.bolla_pdf_ai")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>Estrai seriali da PDF (Gemini 3 Flash)</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>{t("auto.materiale_da_associare")}</Label>
                  <Select value={pdfMaterial} onValueChange={setPdfMaterial}>
                    <SelectTrigger className="rounded-sm" data-testid="pdf-material"><SelectValue placeholder={t("auto.scegli_materiale")} /></SelectTrigger>
                    <SelectContent>{materials.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("auto.committente_di_chi_sono_i_modem")}</Label>
                  <Select value={pdfCommittente || "__none__"} onValueChange={(v) => setPdfCommittente(v === "__none__" ? "" : v)}>
                    <SelectTrigger className="rounded-sm" data-testid="pdf-committente"><SelectValue placeholder={t("auto.scegli_committente")} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">{t("auto.nessuno")}</SelectItem>
                      {committenti.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>File PDF (bolla fornitore)</Label>
                  <Input type="file" accept="application/pdf" onChange={(e) => { setPdfFile(e.target.files?.[0]); setPdfExtracted([]); }} className="rounded-sm" data-testid="pdf-file" />
                </div>
                {pdfExtracted.length > 0 && (
                  <div className="rounded-sm border border-slate-200 bg-slate-50 p-3">
                    <div className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-500">Seriali rilevati ({pdfExtracted.length})</div>
                    <div className="max-h-48 overflow-y-auto">
                      {pdfExtracted.map((s, i) => (
                        <div key={i} className="flex items-center justify-between border-b border-slate-200 py-1 font-mono text-xs">
                          <span>{s}</span>
                          <button onClick={() => setPdfExtracted(pdfExtracted.filter((_, idx) => idx !== i))} className="text-red-600 hover:underline">rimuovi</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                {pdfExtracted.length === 0 ? (
                  <Button onClick={doPdfExtract} disabled={pdfLoading} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="pdf-extract">
                    {pdfLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Estrazione...</> : <>Estrai seriali</>}
                  </Button>
                ) : (
                  <Button onClick={doPdfCommit} className="rounded-sm bg-emerald-600 hover:bg-emerald-700" data-testid="pdf-commit">{t("auto.carica_in_magazzino")}</Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Button variant="ghost" onClick={downloadTemplate} className="rounded-sm text-xs" data-testid="btn-download-template">
            <Download className="mr-1 h-3 w-3" /> {t("auto.template_excel")}
          </Button>
        </div>
      </div>

      {/* Barcode scan bar */}
      <div className="rounded-sm border border-slate-200 bg-white p-4">
        <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
          <ScanLine className="h-4 w-4" /> {t("auto.pistola_barcode_carico_rapido")}
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={scanMaterial} onValueChange={setScanMaterial}>
            <SelectTrigger className="w-56 rounded-sm" data-testid="scan-material"><SelectValue placeholder={t("auto.materiale_x")} /></SelectTrigger>
            <SelectContent>{materials.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}</SelectContent>
          </Select>
          {warehouses.length > 0 && (
            <Select value={scanWarehouse || "__none__"} onValueChange={(v) => setScanWarehouse(v === "__none__" ? "" : v)}>
              <SelectTrigger className="w-40 rounded-sm" data-testid="scan-warehouse"><SelectValue placeholder="Sede" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">{t("auto.nessuna_sede")}</SelectItem>
                {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={scanCommittente || "__none__"} onValueChange={(v) => setScanCommittente(v === "__none__" ? "" : v)}>
            <SelectTrigger className="w-40 rounded-sm" data-testid="scan-committente"><SelectValue placeholder={t("auto.committente_x")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">{t("auto.nessuno")}</SelectItem>
              {committenti.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input
            ref={scanInputRef}
            placeholder={t("auto.scansiona_inserisci_seriale_invio")}
            value={scanValue}
            onChange={(e) => setScanValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doScan()}
            className="flex-1 rounded-sm font-mono"
            data-testid="scan-input"
            autoFocus
          />
          <Button onClick={() => doScan()} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="scan-submit">{t("auto.carica")}</Button>
          <BarcodeScanner
            triggerLabel="Fotocamera"
            testid="mag-open-scanner"
            continuous
            onDetected={(text) => { setScanValue(text); doScan(text); }}
          />
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-slate-400" />
          <Input
            placeholder={t("auto.cerca_seriale")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doSearch()}
            className="w-64 rounded-sm font-mono"
            data-testid="search-serial"
          />
          <Button variant="outline" onClick={doSearch} className="rounded-sm">{t("common.search")}</Button>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Label className="text-xs text-slate-500">{t("auto.committente")}</Label>
          <Select value={committenteFilter} onValueChange={setCommittenteFilter}>
            <SelectTrigger className="w-48 rounded-sm" data-testid="committente-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">{t("auto.tutti_i_committenti")}</SelectItem>
              {committenti.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Label className="text-xs text-slate-500">{t("auto.materiale")}</Label>
          <Select value={materialFilter} onValueChange={setMaterialFilter}>
            <SelectTrigger className="w-56 rounded-sm" data-testid="material-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="__all__">{t("auto.tutti_i_materiali")}</SelectItem>
              {materialsInList.map((name) => <SelectItem key={name} value={name}>{name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Label className="text-xs text-slate-500">{t("auto.stato")}</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 rounded-sm" data-testid="status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="warehouse">{t("auto.in_magazzino")}</SelectItem>
              <SelectItem value="traveling">{t("auto.in_viaggio")}</SelectItem>
              <SelectItem value="installed">Installati</SelectItem>
              <SelectItem value="lost">Perso</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={doExport} className="rounded-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="btn-export-serials">
            <Download className="mr-1 h-3.5 w-3.5" /> {t("auto.esporta_excel")}
          </Button>
        </div>
        {statusFilter === "warehouse" && selected.size > 0 && (
          <>
            <Dialog open={openAssign} onOpenChange={setOpenAssign}>
              <DialogTrigger asChild>
                <Button className="rounded-sm bg-orange-600 hover:bg-orange-700" data-testid="btn-assign">
                  <UserPlus className="mr-2 h-4 w-4" /> Assegna {selected.size} a tecnico
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t("auto.assegna_a_tecnico")}</DialogTitle></DialogHeader>
                <Select value={assignTech} onValueChange={setAssignTech}>
                  <SelectTrigger className="rounded-sm" data-testid="assign-tech-select"><SelectValue placeholder={t("auto.scegli_tecnico")} /></SelectTrigger>
                  <SelectContent>
                    {technicians.map((tech) => <SelectItem key={tech.id} value={tech.id}>{tech.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <DialogFooter>
                  <Button onClick={doAssign} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="assign-confirm">{t("common.confirm")}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Button variant="outline" onClick={doDeleteBulk} className="rounded-sm border-red-300 text-red-700 hover:bg-red-50" data-testid="btn-delete-bulk">
              <Trash2 className="mr-2 h-4 w-4" /> Elimina {selected.size}
            </Button>
          </>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-sm border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs font-bold uppercase tracking-widest text-slate-500">
            <tr>
              <th className="w-10 p-3">
                {statusFilter === "warehouse" && (
                  <Checkbox checked={selected.size > 0 && selected.size === filteredSerials.length} onCheckedChange={toggleAll} data-testid="select-all" />
                )}
              </th>
              <th className="p-3">Seriale</th>
              <th className="p-3">{t("common.material")}</th>
              <th className="p-3">{t("auto.committente_x")}</th>
              <th className="p-3">{t("common.status")}</th>
              <th className="p-3">{t("common.technician")}</th>
              <th className="p-3">{t("common.date")}</th>
              <th className="p-3 text-right">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="p-8 text-center text-slate-400">{t("auto.caricamento")}</td></tr>}
            {!loading && filteredSerials.length === 0 && <tr><td colSpan={9} className="p-8 text-center text-slate-400">{t("auto.nessun_seriale")}</td></tr>}
            {filteredSerials.map((s) => {
              const isInstalled = s.status === "installed";
              const rowCls = isInstalled
                ? "border-b border-emerald-200 bg-emerald-50/60 hover:bg-emerald-100/70"
                : "border-b border-slate-100 hover:bg-slate-50";
              const isEditingComm = editingComm === s.id;
              return (
              <tr key={s.id} className={rowCls} data-testid={`serial-row-${s.id}`}>
                <td className="p-3">
                  {statusFilter === "warehouse" && (
                    <Checkbox
                      checked={selected.has(s.id)}
                      onCheckedChange={(v) => {
                        const next = new Set(selected);
                        if (v) next.add(s.id); else next.delete(s.id);
                        setSelected(next);
                      }}
                      data-testid={`select-${s.serial}`}
                    />
                  )}
                </td>
                <td className="p-3 font-mono font-medium text-slate-900">
                  {isInstalled && <CheckCircle2 className="mr-1 inline-block h-4 w-4 text-emerald-600" />}
                  <button
                    type="button"
                    onClick={() => setEditDialog({ id: s.id, serial: s.serial, material_name: s.material_name || "", committente_id: s.committente_id || "" })}
                    className="inline-flex items-center gap-1 rounded-sm border border-transparent px-1.5 py-0.5 hover:border-slate-300 hover:bg-white"
                    title="Modifica seriale, materiale e committente"
                    data-testid={`edit-serial-btn-${s.id}`}
                  >
                    <span>{s.serial}</span>
                    <Pencil className="h-3 w-3 text-slate-400" />
                  </button>
                </td>
                <td className="p-3">{s.material_name}</td>
                <td className="p-3 text-xs">
                  {isEditingComm ? (
                    <div className="flex items-center gap-1">
                      <Select value={editingCommValue || "__none__"} onValueChange={setEditingCommValue}>
                        <SelectTrigger className="h-7 rounded-sm text-xs" data-testid={`edit-comm-select-${s.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">{t("auto.nessuno")}</SelectItem>
                          {committenti.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => saveComm(s.id)} data-testid={`edit-comm-save-${s.id}`}><Check className="h-3.5 w-3.5 text-emerald-600" /></Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditingComm(null)}><X className="h-3.5 w-3.5 text-slate-500" /></Button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => { setEditingComm(s.id); setEditingCommValue(s.committente_id || "__none__"); }}
                      className="flex items-center gap-1 rounded-sm border border-transparent px-1.5 py-0.5 text-left hover:border-slate-300 hover:bg-white"
                      data-testid={`edit-comm-btn-${s.id}`}
                    >
                      <span>{s.committente_name || <span className="italic text-slate-400">—</span>}</span>
                      <Pencil className="h-3 w-3 text-slate-400" />
                    </button>
                  )}
                </td>
                <td className="p-3">
                  {isInstalled ? (
                    <Badge className="rounded-sm bg-emerald-600 font-mono uppercase text-white hover:bg-emerald-700">{t("auto.installato")}</Badge>
                  ) : s.status === "lost" ? (
                    <Badge className="rounded-sm bg-red-600 font-mono uppercase text-white hover:bg-red-700">Perso</Badge>
                  ) : (
                    <Badge variant="outline" className="rounded-sm font-mono uppercase">
                      {s.status === "warehouse" ? t("auto.in_magazzino") : s.status === "traveling" ? t("auto.in_viaggio") : s.status === "installed" ? t("auto.installato") : s.status}
                    </Badge>
                  )}
                </td>
                <td className="p-3">
                  {s.status === "lost" ? (
                    <span className="text-red-700" title={`Perso da: ${s.lost_from_tech_name || s.technician_name || "?"}`}>
                      {s.lost_from_tech_name || s.technician_name || "—"}
                    </span>
                  ) : (s.technician_name || "—")}
                </td>
                <td className="p-3 font-mono text-xs text-slate-500">
                  {isInstalled ? (
                    <div className="space-y-0.5" data-testid={`installed-info-${s.id}`}>
                      <div className="font-semibold text-emerald-700">
                        {(s.installed_at || "").slice(0, 10)}
                      </div>
                      {s.installed_intervention_ref && (
                        <div className="rounded-sm bg-emerald-100 px-1.5 py-0.5 font-semibold uppercase tracking-wider text-emerald-800" data-testid={`installed-ref-${s.id}`}>
                          IMP: {s.installed_intervention_ref}
                        </div>
                      )}
                      {s.installed_address && <div className="text-emerald-800/70">{s.installed_address}</div>}
                      {s.installed_customer && <div className="text-emerald-800/60">{s.installed_customer}</div>}
                    </div>
                  ) : s.status === "lost" ? (
                    <div className="space-y-0.5" data-testid={`lost-info-${s.id}`}>
                      <div className="font-semibold text-red-700">{(s.lost_at || "").slice(0, 10)}</div>
                      {s.lost_by_name && (
                        <div className="rounded-sm bg-red-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-red-800">
                          Segnato da: {s.lost_by_name}
                        </div>
                      )}
                    </div>
                  ) : (
                    (s.moved_at || s.assigned_at || s.created_at || "").slice(0, 10)
                  )}
                </td>
                <td className="p-3 text-right">
                  {s.status !== "warehouse" && s.status !== "installed" && (
                    <Button size="sm" variant="outline" onClick={() => doUnload(s.serial)} className="rounded-sm mr-1" data-testid={`unload-${s.serial}`}>
                      <PackageMinus className="mr-1 h-3 w-3" /> {t("auto.scarica")}
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => doDelete(s.id, s.serial)} data-testid={`delete-${s.serial}`} title={t("auto.elimina")}>
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>
      {/* Dialog "Seriale già assegnato" con opzione rientro in magazzino */}
      <AlertDialog open={!!returnDialog} onOpenChange={(o) => { if (!o) { setReturnDialog(null); focusScanInput(); } }}>
        <AlertDialogContent data-testid="return-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ArrowLeftRight className="h-5 w-5 text-blue-600" /> Seriale già assegnato
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <div>
                  Il seriale <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 font-mono font-semibold">{returnDialog?.info.serial}</span> risulta già presente:
                </div>
                <div className="rounded-sm border border-slate-200 bg-slate-50 p-3 text-sm">
                  {returnDialog?.info.status === "traveling" && (
                    <div>🚛 <b>Sul furgone di:</b> {returnDialog?.info.technician_name || "—"}</div>
                  )}
                  {returnDialog?.info.status === "lost" && (
                    <div className="text-red-700">❌ <b>{t("auto.segnato_come_perso")}</b> (ultima assegnazione: {returnDialog?.info.technician_name || "—"})</div>
                  )}
                  <div className="mt-1 text-xs text-slate-500">Materiale: {returnDialog?.info.material_name}</div>
                </div>
                <div>Vuoi farlo <b>{t("auto.rientrare_in_magazzino_generale")}</b>?</div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="return-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmReturn} className="bg-[#0052FF] hover:bg-[#0040CC]" data-testid="return-confirm">
              <PackageMinus className="mr-1 h-4 w-4" /> Fai rientrare
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog Modifica seriale: Seriale + Materiale + Committente */}
      <Dialog open={!!editDialog} onOpenChange={(o) => { if (!o) setEditDialog(null); }}>
        <DialogContent data-testid="edit-serial-dialog">
          <DialogHeader>
            <DialogTitle>Modifica seriale</DialogTitle>
          </DialogHeader>
          {editDialog && (
            <div className="space-y-4 py-2">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-widest text-slate-500">Seriale</Label>
                <Input
                  value={editDialog.serial || ""}
                  onChange={(e) => setEditDialog({ ...editDialog, serial: e.target.value })}
                  placeholder="Es. SN-XXXXX"
                  className="rounded-sm font-mono"
                  data-testid="edit-serial-input"
                />
                <div className="text-[11px] text-slate-500">
                  💡 Correggi qui se il barcode è stato letto male. Il sistema verifica che non ci siano duplicati.
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-widest text-slate-500">Materiale</Label>
                <Select
                  value={editDialog.material_name || ""}
                  onValueChange={(v) => setEditDialog({ ...editDialog, material_name: v })}
                >
                  <SelectTrigger className="rounded-sm" data-testid="edit-material"><SelectValue placeholder="Scegli materiale…" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {materials.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input
                  value={editDialog.material_name || ""}
                  onChange={(e) => setEditDialog({ ...editDialog, material_name: e.target.value })}
                  placeholder="…oppure scrivi un nome custom"
                  className="mt-1 rounded-sm text-xs"
                  data-testid="edit-material-custom"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-widest text-slate-500">Committente</Label>
                <Select
                  value={editDialog.committente_id || "__none__"}
                  onValueChange={(v) => setEditDialog({ ...editDialog, committente_id: v === "__none__" ? "" : v })}
                >
                  <SelectTrigger className="rounded-sm" data-testid="edit-committente"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__none__">— Nessuno —</SelectItem>
                    {committenti.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditDialog(null)} disabled={savingSerial}>Annulla</Button>
            <Button
              onClick={() => saveSerial()}
              disabled={savingSerial || !editDialog?.serial?.trim() || !editDialog?.material_name?.trim()}
              className="rounded-sm bg-emerald-600 hover:bg-emerald-700"
              data-testid="edit-serial-save"
            >
              {savingSerial ? "Salvataggio…" : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog conflitto: seriale già presente su altro record → conferma sostituzione */}
      <AlertDialog open={!!serialConflict} onOpenChange={(o) => { if (!o) setSerialConflict(null); }}>
        <AlertDialogContent data-testid="serial-conflict-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ArrowLeftRight className="h-5 w-5 text-amber-600" /> Seriale già presente
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <div>
                  Il seriale <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 font-mono font-semibold">{serialConflict?.newSerial}</span> è già registrato:
                </div>
                <div className="rounded-sm border border-slate-200 bg-slate-50 p-3 text-sm">
                  <div>📦 <b>Materiale:</b> {serialConflict?.existing?.material_name || "—"}</div>
                  <div>
                    📍 <b>Posizione:</b>{" "}
                    {serialConflict?.existing?.status === "warehouse" && "In magazzino generale"}
                    {serialConflict?.existing?.status === "traveling" && `Sul furgone di ${serialConflict?.existing?.technician_name || "un tecnico"}`}
                  </div>
                </div>
                <div className="pt-1 text-xs text-slate-600">
                  Confermando, il record duplicato viene <b>eliminato</b> e il seriale viene assegnato al record che stai modificando.
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="serial-conflict-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={() => saveSerial({ forceReplace: true })} className="bg-[#0052FF] hover:bg-[#0040CC]" data-testid="serial-conflict-confirm">
              Sostituisci e aggiorna
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
